// Shared question bank — used by both triageEngine and GetCareScreen UI.
// Each question has: id, type, label, and (for select) options.
// Types: 'boolean' | 'number' | 'single_select'

export const QUESTIONS = {

  // ── Universal ────────────────────────────────────────────────────────────────

  person_type: {
    id: 'person_type',
    type: 'single_select',
    label: 'Who is this for?',
    options: [
      { value: 'self',  label: 'Me' },
      { value: 'child', label: 'My child' },
      { value: 'other', label: 'Someone else' },
    ],
  },

  age_years: {
    id: 'age_years',
    type: 'number',
    label: 'How old are they?',
    unit: 'years',
    min: 0,
    max: 120,
  },

  duration_days: {
    id: 'duration_days',
    type: 'number',
    label: 'How many days has this been going on?',
    unit: 'days',
    min: 0,
    max: 365,
  },

  worsening: {
    id: 'worsening',
    type: 'single_select',
    label: 'Is it getting better, worse, or staying about the same?',
    options: [
      { value: 'better', label: 'Getting better' },
      { value: 'same',   label: 'About the same' },
      { value: 'worse',  label: 'Getting worse' },
    ],
  },

  fever_present: {
    id: 'fever_present',
    type: 'boolean',
    label: 'Is there a fever?',
  },

  temp_value: {
    id: 'temp_value',
    type: 'number',
    label: "What's the highest temperature?",
    unit: '°F',
    min: 95,
    max: 108,
  },

  temp_method: {
    id: 'temp_method',
    type: 'single_select',
    label: 'How was the temperature taken?',
    options: [
      { value: 'oral',     label: 'By mouth' },
      { value: 'ear',      label: 'In the ear' },
      { value: 'forehead', label: 'Forehead / temporal' },
      { value: 'unknown',  label: 'Not sure' },
    ],
  },

  trouble_breathing: {
    id: 'trouble_breathing',
    type: 'boolean',
    label: 'Any trouble breathing?',
  },

  acting_normal: {
    id: 'acting_normal',
    type: 'boolean',
    label: 'Are they acting normally?',
  },

  drinking_fluids: {
    id: 'drinking_fluids',
    type: 'boolean',
    label: 'Are they able to drink fluids?',
  },

  keeping_fluids_down: {
    id: 'keeping_fluids_down',
    type: 'boolean',
    label: 'Are they able to keep fluids down?',
  },

  urinating_normally: {
    id: 'urinating_normally',
    type: 'boolean',
    label: 'Are they urinating normally?',
  },

  severe_pain: {
    id: 'severe_pain',
    type: 'boolean',
    label: 'Is the pain severe?',
  },

  pain_severity_0_10: {
    id: 'pain_severity_0_10',
    type: 'number',
    label: 'How would you rate the pain from 0 to 10?',
    unit: '/ 10',
    min: 0,
    max: 10,
  },

  blood_present: {
    id: 'blood_present',
    type: 'boolean',
    label: 'Is there any blood?',
  },

  hard_to_wake: {
    id: 'hard_to_wake',
    type: 'boolean',
    label: 'Are they hard to wake up or unusually difficult to rouse?',
  },

  confusion_present: {
    id: 'confusion_present',
    type: 'boolean',
    label: 'Do they seem confused or not making sense?',
  },

  can_bear_weight: {
    id: 'can_bear_weight',
    type: 'boolean',
    label: 'Can they put weight on it or use it normally?',
  },

  swallowing_difficulty: {
    id: 'swallowing_difficulty',
    type: 'boolean',
    label: 'Any trouble swallowing?',
  },

  stiff_neck: {
    id: 'stiff_neck',
    type: 'boolean',
    label: 'Any stiffness or pain when moving the neck?',
  },

  seizure_occurred: {
    id: 'seizure_occurred',
    type: 'boolean',
    label: 'Have there been any seizures or convulsions?',
  },

  vomiting_present: {
    id: 'vomiting_present',
    type: 'boolean',
    label: 'Any vomiting?',
  },

  shortness_of_breath: {
    id: 'shortness_of_breath',
    type: 'boolean',
    label: "Any shortness of breath or feeling like they can't catch their breath?",
  },

  chest_pain: {
    id: 'chest_pain',
    type: 'boolean',
    label: 'Any chest pain?',
  },

  blood_in_stool: {
    id: 'blood_in_stool',
    type: 'boolean',
    label: 'Any blood in the stool?',
  },

  one_sided_weakness: {
    id: 'one_sided_weakness',
    type: 'boolean',
    label: 'Any weakness or numbness on just one side of the body?',
  },

  can_walk_normally: {
    id: 'can_walk_normally',
    type: 'boolean',
    label: 'Are they able to walk normally?',
  },

  is_happening_now: {
    id: 'is_happening_now',
    type: 'boolean',
    label: 'Is this happening right now?',
  },

  sudden_or_gradual: {
    id: 'sudden_or_gradual',
    type: 'single_select',
    label: 'Did it come on suddenly or gradually?',
    options: [
      { value: 'sudden',  label: 'Suddenly' },
      { value: 'gradual', label: 'Gradually over time' },
    ],
  },

  dehydration_signs: {
    id: 'dehydration_signs',
    type: 'boolean',
    label: 'Any signs of dehydration — dry mouth, no tears, very dark urine?',
  },

  // ── Cough / respiratory ──────────────────────────────────────────────────────

  breathing_fast_labored: {
    id: 'breathing_fast_labored',
    type: 'boolean',
    label: 'Is the breathing fast, labored, or noisy?',
  },

  // ── Sore throat ──────────────────────────────────────────────────────────────

  drooling: {
    id: 'drooling',
    type: 'boolean',
    label: 'Any drooling or difficulty handling secretions?',
  },

  throat_one_sided_swelling: {
    id: 'throat_one_sided_swelling',
    type: 'boolean',
    label: 'Any swelling on just one side of the throat or tonsils?',
  },

  // ── Ear pain ─────────────────────────────────────────────────────────────────

  which_ear: {
    id: 'which_ear',
    type: 'single_select',
    label: 'Which ear is bothering them?',
    options: [
      { value: 'left',  label: 'Left ear' },
      { value: 'right', label: 'Right ear' },
      { value: 'both',  label: 'Both ears' },
    ],
  },

  ear_drainage: {
    id: 'ear_drainage',
    type: 'boolean',
    label: 'Any fluid or discharge coming from the ear?',
  },

  hearing_decreased: {
    id: 'hearing_decreased',
    type: 'boolean',
    label: 'Has hearing decreased suddenly?',
  },

  swelling_behind_ear: {
    id: 'swelling_behind_ear',
    type: 'boolean',
    label: 'Any swelling or redness behind the ear?',
  },

  // ── GI / vomiting / diarrhea ─────────────────────────────────────────────────

  type_vomiting_diarrhea: {
    id: 'type_vomiting_diarrhea',
    type: 'single_select',
    label: "What's going on?",
    options: [
      { value: 'vomiting',  label: 'Vomiting only' },
      { value: 'diarrhea',  label: 'Diarrhea only' },
      { value: 'both',      label: 'Both vomiting and diarrhea' },
    ],
  },

  blood_in_vomit_or_stool: {
    id: 'blood_in_vomit_or_stool',
    type: 'boolean',
    label: 'Any blood in the vomit or stool?',
  },

  // ── Abdominal pain ───────────────────────────────────────────────────────────

  pain_location: {
    id: 'pain_location',
    type: 'single_select',
    label: 'Where is the pain mostly located?',
    options: [
      { value: 'upper',       label: 'Upper belly' },
      { value: 'lower',       label: 'Lower belly' },
      { value: 'right_lower', label: 'Lower right side' },
      { value: 'left_lower',  label: 'Lower left side' },
      { value: 'all_over',    label: 'All over' },
    ],
  },

  constant_or_intermittent: {
    id: 'constant_or_intermittent',
    type: 'single_select',
    label: 'Is the pain constant or does it come and go?',
    options: [
      { value: 'constant',     label: 'Constant' },
      { value: 'intermittent', label: 'Comes and goes' },
    ],
  },

  // ── Urinary ──────────────────────────────────────────────────────────────────

  burning_urination: {
    id: 'burning_urination',
    type: 'boolean',
    label: 'Any burning or pain with urination?',
  },

  frequency_urgency: {
    id: 'frequency_urgency',
    type: 'boolean',
    label: 'Needing to urinate more often or urgently than usual?',
  },

  flank_back_pain: {
    id: 'flank_back_pain',
    type: 'boolean',
    label: 'Any pain in the back or sides (flank area)?',
  },

  blood_in_urine: {
    id: 'blood_in_urine',
    type: 'boolean',
    label: 'Any blood in the urine?',
  },

  pregnancy_possible: {
    id: 'pregnancy_possible',
    type: 'boolean',
    label: 'Is there any possibility of pregnancy?',
  },

  // ── Rash / skin ──────────────────────────────────────────────────────────────

  rash_spreading: {
    id: 'rash_spreading',
    type: 'boolean',
    label: 'Is the rash spreading?',
  },

  face_tongue_swelling: {
    id: 'face_tongue_swelling',
    type: 'boolean',
    label: 'Any swelling of the face, lips, or tongue?',
  },

  rash_painful: {
    id: 'rash_painful',
    type: 'boolean',
    label: 'Is the rash painful (not just itchy)?',
  },

  blistering_peeling: {
    id: 'blistering_peeling',
    type: 'boolean',
    label: 'Any blistering or skin peeling?',
  },

  // ── Headache ─────────────────────────────────────────────────────────────────

  neurologic_changes: {
    id: 'neurologic_changes',
    type: 'boolean',
    label: 'Any weakness, numbness, trouble speaking, or vision changes?',
  },

  headache_typical: {
    id: 'headache_typical',
    type: 'boolean',
    label: 'Is this a typical headache for them, or something new and different?',
    // true = typical/familiar, false = new/different
  },

  // ── Minor injury ─────────────────────────────────────────────────────────────

  body_part_injured: {
    id: 'body_part_injured',
    type: 'single_select',
    label: 'What part of the body is injured?',
    options: [
      { value: 'ankle_foot',   label: 'Ankle or foot' },
      { value: 'knee',         label: 'Knee' },
      { value: 'wrist_hand',   label: 'Wrist or hand' },
      { value: 'shoulder_arm', label: 'Shoulder or arm' },
      { value: 'back',         label: 'Back' },
      { value: 'other',        label: 'Other' },
    ],
  },

  major_swelling: {
    id: 'major_swelling',
    type: 'boolean',
    label: 'Is there significant swelling?',
  },

  deformity_present: {
    id: 'deformity_present',
    type: 'boolean',
    label: 'Does anything look bent, out of place, or deformed?',
  },

  open_wound: {
    id: 'open_wound',
    type: 'boolean',
    label: 'Is there an open wound or broken skin?',
  },

  numbness_present: {
    id: 'numbness_present',
    type: 'boolean',
    label: 'Any numbness or tingling?',
  },

  // ── Chest discomfort ─────────────────────────────────────────────────────────

  pain_type: {
    id: 'pain_type',
    type: 'single_select',
    label: 'How would you describe the feeling in the chest?',
    options: [
      { value: 'pressure', label: 'Pressure or squeezing' },
      { value: 'sharp',    label: 'Sharp or stabbing' },
      { value: 'burning',  label: 'Burning' },
      { value: 'other',    label: 'Hard to describe' },
    ],
  },

  sweating_nausea_dizziness: {
    id: 'sweating_nausea_dizziness',
    type: 'boolean',
    label: 'Any sweating, nausea, or dizziness along with the chest discomfort?',
  },

  pain_spreading_arm_jaw_back: {
    id: 'pain_spreading_arm_jaw_back',
    type: 'boolean',
    label: 'Does the pain spread to the arm, jaw, shoulder, or back?',
  },

  known_heart_history: {
    id: 'known_heart_history',
    type: 'boolean',
    label: 'Any known heart conditions?',
  },

  worse_with_movement: {
    id: 'worse_with_movement',
    type: 'boolean',
    label: 'Does it get worse when you move or press on the chest?',
  },

  // ── Shortness of breath ──────────────────────────────────────────────────────

  severity_mild_moderate_severe: {
    id: 'severity_mild_moderate_severe',
    type: 'single_select',
    label: 'How would you describe the breathing difficulty?',
    options: [
      { value: 'mild',     label: 'Mild — noticeable but manageable' },
      { value: 'moderate', label: 'Moderate — hard to do normal activities' },
      { value: 'severe',   label: 'Severe — struggling to breathe' },
    ],
  },

  worse_than_usual: {
    id: 'worse_than_usual',
    type: 'boolean',
    label: 'Is it worse than usual for them?',
  },

  can_speak_full_sentences: {
    id: 'can_speak_full_sentences',
    type: 'boolean',
    label: 'Are they able to speak in full sentences without stopping to catch their breath?',
  },

  lips_face_blue: {
    id: 'lips_face_blue',
    type: 'boolean',
    label: 'Do the lips or face look bluish or very pale?',
  },

  wheezing: {
    id: 'wheezing',
    type: 'boolean',
    label: 'Any wheezing or whistling sound when breathing?',
  },

  asthma_lung_history: {
    id: 'asthma_lung_history',
    type: 'boolean',
    label: 'Any history of asthma, COPD, or other lung conditions?',
  },

  // ── Dizziness ────────────────────────────────────────────────────────────────

  passed_out_fainted: {
    id: 'passed_out_fainted',
    type: 'boolean',
    label: 'Have they passed out or fainted?',
  },

  severe_headache: {
    id: 'severe_headache',
    type: 'boolean',
    label: 'Is there also a severe headache?',
  },

  symptoms_when_standing_only: {
    id: 'symptoms_when_standing_only',
    type: 'boolean',
    label: 'Does it mostly happen when standing up quickly?',
  },

  // ── Allergic reaction ────────────────────────────────────────────────────────

  swelling_lips_tongue_throat: {
    id: 'swelling_lips_tongue_throat',
    type: 'boolean',
    label: 'Any swelling of the lips, tongue, or throat?',
  },

  hives_present: {
    id: 'hives_present',
    type: 'boolean',
    label: 'Any hives — raised, itchy welts on the skin?',
  },

  vomiting_or_dizziness: {
    id: 'vomiting_or_dizziness',
    type: 'boolean',
    label: 'Any vomiting or dizziness along with the reaction?',
  },

  known_allergen_exposure: {
    id: 'known_allergen_exposure',
    type: 'boolean',
    label: 'Was there known exposure to an allergen — food, medication, insect sting?',
  },

  symptoms_spreading_quickly: {
    id: 'symptoms_spreading_quickly',
    type: 'boolean',
    label: 'Are the symptoms spreading or getting worse quickly?',
  },

  took_epinephrine: {
    id: 'took_epinephrine',
    type: 'boolean',
    label: 'Was an epinephrine auto-injector (like an EpiPen) used?',
  },

  // ── Constipation ─────────────────────────────────────────────────────────────

  days_since_bowel_movement: {
    id: 'days_since_bowel_movement',
    type: 'number',
    label: 'How many days since a normal bowel movement?',
    unit: 'days',
    min: 0,
    max: 30,
  },

  severe_abdominal_pain: {
    id: 'severe_abdominal_pain',
    type: 'boolean',
    label: 'Any severe abdominal pain?',
  },

  belly_swollen_hard: {
    id: 'belly_swollen_hard',
    type: 'boolean',
    label: 'Is the belly swollen or feel hard?',
  },

  passing_gas: {
    id: 'passing_gas',
    type: 'boolean',
    label: 'Are they still able to pass gas?',
  },

  // ── Joint pain ───────────────────────────────────────────────────────────────

  which_joint: {
    id: 'which_joint',
    type: 'single_select',
    label: 'Which joint is affected?',
    options: [
      { value: 'knee',       label: 'Knee' },
      { value: 'ankle',      label: 'Ankle' },
      { value: 'hip',        label: 'Hip' },
      { value: 'shoulder',   label: 'Shoulder' },
      { value: 'wrist_hand', label: 'Wrist or hand' },
      { value: 'elbow',      label: 'Elbow' },
      { value: 'other',      label: 'Other' },
    ],
  },

  injury_recent: {
    id: 'injury_recent',
    type: 'boolean',
    label: 'Was there a recent injury or fall?',
  },

  redness_warmth: {
    id: 'redness_warmth',
    type: 'boolean',
    label: 'Is the joint red or warm to the touch?',
  },

  swelling_present: {
    id: 'swelling_present',
    type: 'boolean',
    label: 'Is there visible swelling?',
  },

  sudden_onset: {
    id: 'sudden_onset',
    type: 'boolean',
    label: 'Did this come on suddenly?',
  },

  // ── Vaginal symptoms ─────────────────────────────────────────────────────────

  discharge_present: {
    id: 'discharge_present',
    type: 'boolean',
    label: 'Is there unusual discharge?',
  },

  itching_irritation: {
    id: 'itching_irritation',
    type: 'boolean',
    label: 'Any itching or irritation?',
  },

  unusual_odor: {
    id: 'unusual_odor',
    type: 'boolean',
    label: 'Any unusual odor?',
  },

  pelvic_pain: {
    id: 'pelvic_pain',
    type: 'boolean',
    label: 'Any pain in the pelvic area or lower abdomen?',
  },

  bleeding_unusual: {
    id: 'bleeding_unusual',
    type: 'boolean',
    label: 'Any unusual bleeding?',
  },

  pain_with_urination: {
    id: 'pain_with_urination',
    type: 'boolean',
    label: 'Any pain with urination?',
  },

  // ── Rectal bleeding ──────────────────────────────────────────────────────────

  blood_amount: {
    id: 'blood_amount',
    type: 'single_select',
    label: 'How much blood is there?',
    options: [
      { value: 'small_streak', label: 'A small streak or spot' },
      { value: 'moderate',     label: 'Moderate amount' },
      { value: 'large',        label: 'A large amount' },
    ],
  },

  black_tarry_stool: {
    id: 'black_tarry_stool',
    type: 'boolean',
    label: 'Is the stool black or tarry-looking?',
  },

  dizziness_weakness: {
    id: 'dizziness_weakness',
    type: 'boolean',
    label: 'Any dizziness or unusual weakness?',
  },

  known_hemorrhoids: {
    id: 'known_hemorrhoids',
    type: 'boolean',
    label: 'Any known hemorrhoids?',
  },

  recurring: {
    id: 'recurring',
    type: 'boolean',
    label: 'Has this happened more than once?',
  },

  // ── Palpitations ─────────────────────────────────────────────────────────────

  heart_rate_very_fast: {
    id: 'heart_rate_very_fast',
    type: 'boolean',
    label: "Does the heart feel like it's beating very fast?",
  },

  fainting_near_fainting: {
    id: 'fainting_near_fainting',
    type: 'boolean',
    label: 'Any fainting or near-fainting?',
  },

  duration_minutes: {
    id: 'duration_minutes',
    type: 'number',
    label: 'About how many minutes has this been going on?',
    unit: 'minutes',
    min: 0,
    max: 600,
  },

  caffeine_stimulant_trigger: {
    id: 'caffeine_stimulant_trigger',
    type: 'boolean',
    label: 'Any recent caffeine, energy drinks, or stimulants?',
  },

  // ── Weakness / fatigue ───────────────────────────────────────────────────────

  vomiting_or_diarrhea: {
    id: 'vomiting_or_diarrhea',
    type: 'boolean',
    label: 'Any vomiting or diarrhea along with the weakness?',
  },
};

// Derive age band from age in years
export function getAgeBand(ageYears) {
  if (ageYears == null) return 'adult';
  if (ageYears < 2)  return 'infant';
  if (ageYears < 13) return 'child';
  if (ageYears < 18) return 'teen';
  if (ageYears < 65) return 'adult';
  return 'older_adult';
}
